import { formatCompactINR, formatINR } from "@/lib/format";
import { Target, Wallet, Rocket } from "lucide-react";
import DownloadPdfButton from "@/components/site/DownloadPdfButton";

export default function ResultsSection({ plan, inputs, leadId, entered }) {
  const mask = (formatted) => (entered ? formatted : "—");

  return (
    <section
      className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#111827] to-[#0A0F1C] overflow-hidden"
      data-testid="results-section"
    >
      <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#F97316]/20 blur-3xl pointer-events-none" />

      <div className="relative p-6 sm:p-8 lg:p-10">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F97316]">
          <Target className="w-3.5 h-3.5" />
          Target Passive Income Corpus
        </div>

        <div className="mt-4" data-testid="target-corpus-display">
          <div className="font-heading text-5xl sm:text-6xl font-semibold tracking-tighter num-font bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent">
            {mask(formatCompactINR(plan.targetCorpus))}
          </div>
          <div className="mt-2 text-xs text-slate-400 num-font">
            = {mask(formatINR(plan.targetCorpus))}
          </div>
        </div>

        <p className="mt-5 text-sm text-slate-400 leading-relaxed">
          At age <span className="text-white">{inputs.freedomAge}</span>, your monthly expenses
          will grow to{" "}
          <span className="text-[#F97316] font-medium num-font">
            {mask(formatINR(plan.futureMonthlyExpense))}
          </span>
          . The above corpus sustains that — <em>forever</em>, adjusted for inflation.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-4" data-testid="sip-results">
          <div className="rounded-xl border border-white/10 bg-white/5 p-5">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-400">
              <Wallet className="w-3.5 h-3.5" />
              Normal SIP
            </div>
            <div
              className="mt-2 font-heading text-2xl sm:text-3xl font-semibold num-font text-white"
              data-testid="normal-sip-amount"
            >
              {mask(formatINR(plan.normalSip))}
              <span className="text-sm font-normal text-slate-500 ml-1">/ month</span>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Fixed amount for {inputs.freedomAge - inputs.currentAge} years
            </div>
          </div>

          <div className="rounded-xl border border-[#F97316]/30 bg-[#F97316]/10 p-5 relative">
            <div className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.2em] bg-[#F97316] text-white px-2 py-1 rounded">
              Recommended
            </div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#FB923C]">
              <Rocket className="w-3.5 h-3.5" />
              Step-up SIP • {Math.round(inputs.stepupPct * 100)}% / year
            </div>
            <div
              className="mt-2 font-heading text-2xl sm:text-3xl font-semibold num-font text-white"
              data-testid="stepup-sip-amount"
            >
              {mask(formatINR(plan.stepupSip))}
              <span className="text-sm font-normal text-slate-400 ml-1">/ month (start)</span>
            </div>
            <div className="mt-1 text-xs text-[#FDBA74]">
              Grows by {Math.round(inputs.stepupPct * 100)}% annually with your income
            </div>
          </div>
        </div>

        <div
          className="mt-8 rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-[11px] text-slate-400 leading-relaxed"
          data-testid="download-disclaimer"
        >
          <span className="text-[#FDBA74] font-medium">Disclaimer:</span> Mutual fund investments
          are subject to market risks. Please read all scheme related documents carefully.
        </div>

        <div className="mt-3">
          <DownloadPdfButton
            inputs={inputs}
            leadId={leadId}
            entered={entered}
            variant="primary"
            testId="download-pdf-btn"
          />
        </div>
        <div className="mt-3 text-center text-[11px] text-slate-500">
          A Sahi Disha advisor will call you
        </div>
      </div>
    </section>
  );
}
